<template>
  <b-modal
    id="vendor-modal"
    ref="modal"
    class="modal"
    size="lg"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
  >
    <template
      slot="modal-header"
    >
      <span>
        <h5>
          Upload Vendors
        </h5>
        <p>
          Upload a list of your current vendors in any format. You can also forward your vendor list and contracts to <a href="mailto:contracts@meetvirginia.io">contracts@meetvirginia.io</a> and they will be added automatically.
        </p>
      </span>

      <button
        class="btn btn--close"
        @click="hide()"
      >
        <font-awesome-icon
          :icon="['fas', 'times']"
          class="icon alt"
        />
      </button>
    </template>

    <form-component>
      <form
        novalidate="novalidate"
        @submit.prevent="doSave"
      >
        <div
          class="card mb-4 avatar-card"
          @click="openFilePicker"
        >
          <div class="row">
            <div class="col-12 col-lg-6 order-lg-1">
              <button
                type="button"
                class="btn btn-primary btn-upload"
                style="min-width: 200px;"
              >
                <span v-if="savingFiles && !dragAndDropSavingFiles">
                  <font-awesome-icon
                    :spin="true"
                    :icon="['fas', 'spinner']"
                    class="icon alt"
                  />
                </span>
                <span v-else>
                  <font-awesome-icon
                    :icon="['fas', 'upload']"
                    class="icon alt"
                  />
                  Upload
                </span>
              </button>
            </div>
            <div class="col-12 col-lg-6 order-lg-2">
              <div class="img-holder">
                <div
                  class="no-image"
                  @dragover="dragover"
                  @dragleave="dragleave"
                  @drop="drop"
                >
                  <span v-if="savingFiles && dragAndDropSavingFiles">
                    <font-awesome-icon
                      :spin="true"
                      :icon="['fas', 'spinner']"
                      class="icon alt"
                    />
                  </span>
                  <p v-else>
                    Drag and Drop File
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <input
          id="photoFile"
          ref="photoFilePicker"
          style="display:none;"
          type="file"
          name="photoFile"
          @change="willAddFile"
        >
        <div
          class="form-row"
        >
          <div
            v-if="files.length"
            class="col-12"
          >
            <div class="uploadedfiles">
              <label class="uploaded-files-header"> Uploaded Files </label>
              <div
                v-for="(file, index) in files"
                :key="index"
              >
                <label> {{ file.filename }} </label>
                <a
                  :href="file.url"
                  target="_blank"
                  class="download-file"
                >
                  <i
                    class="fa fa-download"
                    aria-hidden="true"
                  />
                </a>
                <span
                  style="cursor:pointer;"
                  @click="removeFile(file)"
                >
                  <i
                    class="fa fa-trash"
                    aria-hidden="true"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </form-component>

    <template
      slot="modal-footer"
    >
      <button
        class="btn"
        @click="hide()"
      >
        Cancel
      </button>
      <button
        v-if="editModel"
        type="button"
        :disabled="removing"
        class="btn btn--secondary"
        @click="deleteVendor(editModel.id)"
      >
        <processing-text :prc="removing">
          Remove
        </processing-text>
      </button>
      <button
        type="button"
        :disabled="saving || savingFiles"
        class="btn btn--primary"
        @click="doSave()"
      >
        <processing-text :prc="saving">
          Save
        </processing-text>
      </button>
    </template>
  </b-modal>
</template>

<script>
import FormComponent from '@/components/FormComponent.vue';
import ProcessingText from '@/components/ProcessingText.vue';
import UserClient from '@/api/UserClient';

export default {
  name: 'VendorListUploadModal',
  components: {
    FormComponent,
    ProcessingText,
  },
  props: {
    onFilesSaved: {
      type: Function,
    },
  },
  data() {
    return {
      saving: false,
      userClient: new UserClient(this.$http),
      editModel: null,
      savingFiles: false,
      dragAndDropSavingFiles: false,

      files: [],
    };
  },
  methods: {
    async doSave() {

      this.saving = true;

      try {
        await this.userClient.setVendorListUploadedFiles(this.files);

        this.$globals.showSuccess('File(s) Uploaded!');

        this.onFilesSaved(this.files);
        this.hide();
      } catch(ex) {
        console.log(ex);
        this.$globals.showError(`Failed to save upload. Please try again.`);
      } finally {
        this.saving = false;
      }
    },
    show(vendorListUploadFiles) {
      this.files = vendorListUploadFiles;

      this.editModel = null;

      this.saving = false;
      this.savingFiles = false;
      this.dragAndDropSavingFiles = false;

      this.$refs.modal.show();
    },
    hide() {
      this.saving = false;
      this.$refs.modal.hide();
    },
    openFilePicker() {
      if (this.savingFiles) return;

      this.$refs.photoFilePicker.click();
    },
    willAddFile(event) {
      const files = event.target.files || [];
      this.dragAndDropSavingFiles = false;
      this.saveFiles(files);
    },
    async saveFiles(files) {
      if (!files || !files.length) {
        this.savingFiles = false;
        this.dragAndDropSavingFiles = false;
      }

      this.savingFiles = true;

      try {
        for (let x = 0; x<files.length; x++) {
          const file = files[x];
          const fileName = file.name;
          const signed_url = await this.$globals.userClient.getFileUrl(`current_vendors/${new Date().getTime()}/${fileName}`);
          await UserClient.saveFile(signed_url, file);
          const fileUrl = signed_url.split('?')[0] + `?dt=${new Date().getTime()}`;

          this.files.push({
            filename: fileName,
            url: fileUrl,
          })

        }
      } catch (ex) {
        console.log(ex);
        this.$globals.showError('Failed to save file. Please try again!', ex);
      }

      this.savingFiles = false;
      this.dragAndDropSavingFiles = false;
      this.$refs.photoFilePicker.value = '';

    },
    removeFile(file) {
      this.files = this.files.filter(f => f != file);
    },
    dragover(event) {
      // Add some visual fluff to show the user can drop its files
      if (!event.currentTarget.classList.contains('drag-hover')) {
        event.currentTarget.classList.add('drag-hover');
      }
      event.preventDefault();
    },
    dragleave(event) {
      event.currentTarget.classList.remove('drag-hover');
    },
    drop(event) {
      if (this.savingFiles) return;
      event.preventDefault();
      const files = event.dataTransfer.files || [];
      this.dragleave(event);

      this.dragAndDropSavingFiles = true;

      setTimeout(() => {
        this.saveFiles(files);
      }, 10);
    }
  },
};
</script>

<style scoped lang="scss">
.modal-body {
  padding-top: 0;
}

.uploaded-files-header {
  font-size: 18px;
  font-weight: 700;
}

a.download-file {
  margin: 22px;
}

.drag-hover {
  background: #0d3b6688 !important;
  border: dashed 3px #0d3b66 !important;
}
</style>
